/**
 * folderOwnership — 폴더의 소유(내 것 / 공유받은 것) 판정과 그 문구. **순수 함수다**(React 를 모른다).
 *
 * 떼어 둔 이유는 두 가지다.
 *
 * ── 하나. 문구가 흩어져 있었다 ────────────────────────────────────────────
 * 「다른 사람이 만든 …」이라는 같은 말이 FolderTree.jsx 안에 세 자리로 흩어져 있었다
 * (메뉴 안내 · 옮길 곳 목록의 사유 · 그 사유의 설명). 한 자리만 고치면 화면이 같은 사실을
 * 서로 다른 말로 말하는데, **빌드는 통과하고 화면도 뜬다.** 그래서 값의 출처를 하나로 모은다.
 *
 * ── 둘. 판정이 검산 대상이다 ─────────────────────────────────────────────
 * 소유 판정이 틀리면 화면이 「고칠 수 있는 폴더를 잠그거나」 「남의 폴더를 고칠 수 있게」 보인다.
 * 둘 다 조용히 일어난다 — 렌더 코드 사이에 섞여 있으면 입력과 출력만 놓고 볼 수가 없다.
 * 여기 있으면 함수 호출만으로 검산한다(folderOwnership.test.js).
 *
 * ── 모르면 잠그지 않는다 ─────────────────────────────────────────────────
 * `is_mine` 은 서버가 항목·tree 노드마다 싣는다(BE-20260829-reply-fe-0828.md §3 3-1).
 * 값이 없는 경우(옛 배포분 응답 · 트리 밖 id · 아직 안 읽은 상태)는 **참으로 본다.**
 * 트리 전체가 읽기 전용으로 굳어 고칠 수 있는 폴더까지 막히는 편이 더 나쁘기 때문이다.
 *
 * ── 무리는 최상위가 정한다 ───────────────────────────────────────────────
 * 「이 폴더가 어느 무리(내 폴더 / 공유받은 폴더)에 속하는가」는 제 `is_mine` 이 아니라
 * **최상위 조상의 `is_mine`** 이다. 무리 나눔이 최상위 줄에만 걸리기 때문이다(설계 §4 FS-02) —
 * 남의 폴더 안에 있는 내 하위 폴더는 트리에서 「공유받은 폴더」 아래에 그려진다.
 * 그 계산도 여기서만 한다(buildGroupIndex · groupOf). 화면마다 다시 쓰면 같은 폴더가
 * 트리에서는 「공유받은」, 고르기 창에서는 「내」 무리에 뜨는데 **빌드는 통과한다.**
 *
 * ── 축이 셋이다 — 한 축으로 세 사실을 말하지 않는다 ──────────────────────
 * `is_mine`(누가 만들었나) · `permission`(내가 이 폴더에 무엇을 할 수 있나) ·
 * `all_teachers`(내가 이 폴더를 교사 전원에게 공유했나)는 **서로 다른 축이다.**
 * edit 로 공유받은 폴더는 「남이 만든 것」이면서 「고칠 수 있는 것」이고, 매니저에게는 남의 폴더가
 * 전부 edit 다. 그래서 판정도(isMineNode · canPutPapersNode · shareStateOf) 문구도
 * (SHARED_LOCK · PAPER_LOCK) 축마다 따로 둔다 — 하나로 합치면 그중 둘이 조용히 거짓이 된다.
 * 계약: BE-20260910-reply-teacher-access-folder-sharing.md §2 ·
 * BE-20260910-reply-share-all-teachers.md §2-3.
 */

/** 내가 만든 폴더인가 — 모르면 잠그지 않는다(지금 FolderTree.jsx:369 와 같은 규칙). */
export const isMineNode = (node) => node?.is_mine !== false;

/**
 * 시험지를 넣고 뺄 수 있는가 — **축은 `permission` 이다. `is_mine` 이 아니다.**
 *
 * (가) 막을 것이 정확히 view 다. 계약이 닫는 것은 등급 view 하나뿐이고
 *      (BE-20260910-reply-teacher-access-folder-sharing.md §2-3 — 시험지 넣기·빼기(move)·
 *      복사해 넣기·하위 폴더 만들기는 owner·edit 만), owner·edit 는 서버가 통과시킨다.
 * (나) `is_mine` 으로 막으면 **매니저가 남의 폴더에 넣던 기존 동작까지 닫힌다.** 매니저는 남의
 *      폴더도 항상 `permission:'edit'` 이고(§2-1 「제한은 없음」) 서버가 허용한다 — 화면만
 *      잠그면 되던 일이 안 되고, 왜 안 되는지가 화면 어디에도 없다.
 * (다) 운영 배포분 응답에는 이 키가 아직 없다. 없으면 `undefined !== 'view'` 로 **전부 참** —
 *      **화면이 한 곳도 바뀌지 않는다.** 이 파일 머리의 「모르면 잠그지 않는다」와 같은 규칙이다.
 *      새 필드를 모르는 서버 앞에서 트리를 잠그는 편이 더 나쁘다.
 */
export const canPutPapersNode = (node) => node?.permission !== 'view';

/**
 * 「교사 전원」 공유가 이 폴더에 걸려 있는가 — **세 값을 가린다.** 돌려주는 것은 계약의
 * `all_teachers`(직접 걸린 것만. 하위는 상속받아도 null) 그대로다.
 *
 * | 돌려주는 값 | 뜻 | 화면이 하는 일 |
 * |---|---|---|
 * | `'view'` · `'edit'` | 걸려 있다 | 「공유 중」 표시 · [공유 취소] |
 * | `null` | 걸려 있지 않다(해제됨 · 한 번도 안 걸었음) | [공유] |
 * | `undefined` | **서버가 이 필드를 모른다**(운영 배포분) | [공유] 항목 자체를 감춘다(오너 규칙 6) |
 *
 * 그래서 `!= null` 로 읽지 않는다 — 그렇게 보면 뒤의 둘이 한 값으로 뭉개져 「해제됨」과
 * 「서버가 모름」이 구별되지 않고, 이 경로를 모르는 서버에서도 [공유] 가 뜬다. 눌러도 되는 일이
 * 없으니 화면이 없는 기능을 있다고 말하게 된다. 키의 유무로 가르는 것은 이 파일의 다른 방어와
 * 같은 종류다 — isMineNode 도 `is_mine` 이 **명시로 false 일 때만** 남의 것으로 본다.
 *
 * `is_shared` 를 쓰지 않는다. 그 값은 `share_count > 0 || all_teachers != null` 로
 * **개별 공유까지 합친 것**이라(계약 §2-3), 개별 공유가 걸려 있으면 [공유 취소] 뒤에도 참이다.
 * 화면이 켜고 끄는 것은 `all_teachers` 하나뿐이므로(취소가 `items` 키를 보내지 않는다) 표시도
 * 그것으로 해야 「공유 중」이 방금 누른 버튼과 맞는다.
 */
export const shareStateOf = (node) => (
  Object.prototype.hasOwnProperty.call(node ?? {}, 'all_teachers') ? (node.all_teachers ?? null) : undefined
);

/** 작성자 이름 — 없거나 빈 문자열이면 null(계약상 삭제된 사용자는 null 로 온다). */
export const authorLabel = (node) => {
  const v = node?.author_name;
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
};

/**
 * 최상위 줄을 두 무리로 — 각 무리 안의 순서는 **서버가 준 그대로** 둔다(정렬 규칙은 서버에 있다).
 * 여기서 다시 정렬하면 같은 목록에 두 개의 순서 규칙이 생기고, 서버가 규칙을 바꿔도 화면이 안 따른다.
 * 배열이 아니면 빈 배열로 받친다 — 조회가 실패하거나 봉투가 낡아 와도 화면이 터지지 않아야 한다.
 */
export function splitTopLevel(tree) {
  const list = Array.isArray(tree) ? tree : [];
  return {
    mine: list.filter(isMineNode),
    shared: list.filter((n) => !isMineNode(n)),
  };
}

/**
 * 평면 목록 → 「이 폴더가 어느 무리에 속하는가」 지도. Map(String(folder_id) → 'mine' | 'shared').
 *
 * 판정은 **최상위 조상의 `isMineNode`** 다. 무리 나눔은 최상위 줄에만 걸리므로(FS-02),
 * 남의 최상위 폴더 안에 있는 내 하위 폴더도 「공유받은 폴더」 무리에 함께 있다.
 * 하위 폴더 자기 자신의 `is_mine` 만 보면 같은 폴더가 화면마다 다른 무리에 뜬다.
 *
 * 입력은 **평면 목록**이다 — GET /folders 의 `items`(folder_id · parent_id · is_mine, 계약 §2).
 * 중첩 tree 가 아니라 items 를 받는 이유는 tree 를 안 쓰는 화면(시험지 고르기 창)도 같은
 * 판정을 써야 하기 때문이다. 최상위까지 한 번에 올라가려면 부모를 가리키는 값이 필요하다.
 *
 * 망가진 데이터에도 지도는 만든다 — examPaperApi.js 의 buildFolderTree · buildFolderPathIndex 와 같은 방어다:
 *  - **고아**(parent_id 가 목록에 없음. mine=1 로 부모만 걸러진 경우가 실제로 난다) →
 *    거기서 멈춰 **그 폴더를 최상위로 본다.** 트리도 고아를 최상위로 올린다.
 *  - **순환**(A→B→A. 서버가 막지만 백필·수동 수정으로 생길 수 있다) → 고리에 걸린 줄은
 *    **자기 자신을 최상위로 본다**(buildFolderTree 가 고리에 걸린 노드를 최상위로 올리는 것과 같은 결말).
 *    seen 이 없으면 while 이 끝나지 않아 탭이 멈춘다.
 *  - **id 없는 항목** → 지도에 넣지 않는다. 'null' 을 키로 쓰면 서로 다른 무명 폴더가 한 칸을 나눠 쓴다.
 */
export function buildGroupIndex(items) {
  const byId = new Map();
  (Array.isArray(items) ? items : []).forEach((f) => {
    const id = f?.folder_id ?? f?.id;
    if (id != null) byId.set(String(id), f);
  });
  const out = new Map();
  byId.forEach((node, key) => {
    const seen = new Set();
    let top = node;
    let cur = key;
    while (cur != null) {
      if (seen.has(cur)) { top = node; break; } // 순환 — 최상위를 정할 수 없으니 자기 자신으로 본다
      seen.add(cur);
      const n = byId.get(cur);
      if (n == null) break; // 고아 — 직전에 밟은 폴더가 이 가지의 최상위다
      top = n;
      const parent = n.parent_id;
      cur = parent == null ? null : String(parent);
    }
    out.set(key, isMineNode(top) ? 'mine' : 'shared');
  });
  return out;
}

/**
 * 지도에서 무리를 꺼낸다 — **모르면 'mine'.**
 * 이 파일의 기존 태도와 같다(isMineNode). 지도에 없는 id(아직 안 읽은 목록 · 목록 밖 폴더 ·
 * 지도를 못 만든 화면)를 'shared' 로 보면, 화면이 고칠 수 있는 폴더를 남의 것으로 그리고 잠근다.
 */
export const groupOf = (groupIndex, id) => {
  if (id == null || typeof groupIndex?.get !== 'function') return 'mine';
  return groupIndex.get(String(id)) === 'shared' ? 'shared' : 'mine';
};

/** 공유 폴더 문구 — 지금 세 자리에 흩어져 있는 말을 여기서만 정한다. */
export const SHARED_LOCK = {
  menu: '다른 사람의 공유 폴더라 볼 수만 있습니다.',
  moveReason: '공유 폴더',
  moveTitle: '다른 사람의 폴더라 옮겨 넣을 수 없습니다',
};

/**
 * 등급이 막는 것의 문구 — **SHARED_LOCK 에 섞지 않는다.**
 * SHARED_LOCK 은 「남이 만든 것」(is_mine)의 말이고, 이것은 「등급」(permission)의 말이다.
 * 한 상수에 두 축을 담으면 edit 로 공유된 폴더에서 두 문구가 **동시에 거짓**이 된다 —
 * 남이 만든 것은 맞는데 넣을 수는 있으니, 「남이 만든 폴더라 넣을 수 없습니다」가 두 번 틀린다.
 * 두 칸의 자리도 SHARED_LOCK 과 같다 — reason 은 옮길 곳 목록의 짧은 사유, title 은 그 설명이다.
 *
 * **title 이 둘인 이유** — 같은 등급이 막는 일이 두 방향이다(계약 §2-3 은 「넣기·빼기(move)」를
 * 한 줄로 묶어 두었지만, 화면에서는 서로 다른 자리에서 걸린다).
 *   title        … **들어오는** 쪽. 이 폴더가 **도착지**일 때 — 드롭 대상 줄 · 옮길 곳 목록 · 사본을 둘 폴더
 *   takeOutTitle … **나가는** 쪽. 이 폴더가 **출발지**일 때 — 행을 끌기 시작하는 자리 · 행의 [이동] 버튼
 * 한 문장으로 뭉개면 둘 중 하나가 반드시 거짓이 된다 — 출발지에서 「넣을 수 없습니다」가 뜨면
 * 사용자는 넣으려던 적이 없으므로 무엇이 막혔는지 알 수 없다.
 * 나가는 쪽 문장이 「빼기」가 아니라 「옮기기」인 것은, 그 자리가 [없음]으로 빼는 것과
 * 다른 폴더로 옮기는 것을 **함께** 막기 때문이다.
 */
export const PAPER_LOCK = {
  reason: '보기 전용',
  title: '보기 전용 폴더라 넣을 수 없습니다',
  takeOutTitle: '보기 전용 폴더라 옮길 수 없습니다',
};

/**
 * 줄에 붙는 설명 — 만든 사람을 아는 경우와 모르는 경우(FS-03).
 * 이름이 없다고 설명을 지우지 않는다. 「공유 폴더라 볼 수만 있다」는 사실은 이름과 무관하게 참이고,
 * 설명이 없으면 사용자는 왜 손잡이가 없는지를 화면 밖에서 짐작해야 한다.
 */
export const sharedRowTitle = (name) => (name
  ? `${name}의 공유 폴더 · 보기 전용`
  : '공유 폴더 · 보기 전용');

/**
 * 내가 공유한 줄의 설명 — sharedRowTitle 과 **짝이다.** 저쪽은 받은 쪽의 말, 이쪽은 보낸 쪽의 말.
 *
 * 등급으로 갈라 놓는 이유: 화면이 만드는 값은 view 고정이지만(오너 규칙 2) **계약은 edit 도 준다**
 * (§2-3 `all_teachers: 'view'|'edit'`). 다른 화면·서버 작업·손으로 edit 가 걸린 폴더에서
 * 「보기만 합니다」가 뜨면 화면이 거짓말을 한다 — 그 폴더의 선생님은 실제로 고칠 수 있다.
 * 「고칠 수도」로 적는 것은 계약이 보증하는 만큼만 말하기 위해서다(개별 공유가 겹치면 등급은 높은
 * 쪽이라, edit 인 사람이 누구까지인지는 이 값 하나로 알 수 없다 — §3 Q1).
 *
 * 이 말이 붙는 표시는 **작은 사람 아이콘(lucide Users) 하나**다(오너 지시 — 「현재 있는 작은 사람
 * 아이콘으로 충분함」, 숫자는 쓰지 않는다). ⚠ 이것은 **이 화면의 기존 규칙과 어긋난다**:
 * FolderTree.jsx 는 Users 를 이미 「공유받은 폴더(남의 것)」 뜻으로 쓰고 있고(줄 손잡이),
 * 같은 그림이 두 뜻을 맡으면 안 된다고 주석으로 두 번 판정해 뒀다(그래서 [공유] 메뉴 항목과
 * 「전체」 필터는 Users 를 피했다). 오너 지시가 그 판정보다 앞서므로 **자리로 가른다** —
 * 새 표시는 이름 버튼 안(이름 바로 뒤)이고 손잡이 자리와 열이 다르다. 사실로 남겨 두는 이유는,
 * 다음에 이 그림을 보는 사람이 「규칙을 몰라서 그랬나」로 읽지 않게 하기 위해서다.
 */
export const sharedOutRowTitle = (permission) => (permission === 'edit'
  ? '강사 전원 공유 중 · 편집 가능'
  : '강사 전원 공유 중 · 보기 전용');

/**
 * 「교사 전원」 공유가 쓰는 말 — **여러 자리에서 쓰이는 것만** 여기 둔다.
 * (FolderTree.jsx 의 SHARE_VIEW 는 그 파일 공유 겹 전용 상수다. 겹 안에서 한 번 쓰는 머리글까지
 *  여기로 올리면 이 파일이 화면의 사본이 된다.)
 *
 * 상태가 둘뿐이라 말도 둘이다 — [공유] 와 [공유 취소](오너 규칙 1). 대상은 교사 전원, 등급은
 * view 고정이라(규칙 2) 고를 것이 없고 그래서 고르는 말이 없다.
 *  - `grade` — 등급 선택칸을 두지 않은 대신 그 고정값을 사실로 한 줄 말한다. 없으면 사용자가
 *    「그럼 선생님이 고칠 수도 있나」를 화면 밖에서 짐작해야 한다.
 *  - `scope` — 이 공유가 **어디까지 번지는가**. 최상위에 걸면 하위와 그 안의 시험지가 함께 간다.
 *  - `rootOnly` — 하위 폴더에서 [공유] 를 감추는 이유다(계약: 최상위가 아니면 400 `not_root`).
 *    ⚠ **감추기만 하면 「일부러 막아 놨나」로 끝난다** — 실제로 오너가 그렇게 물었다(2026-09-10).
 *    그래서 하위 폴더의 [⋯] 에는 항목 대신 **이 문장이 선다.** 문구가 「할 수 없다」에서 그치지 않고
 *    **어디서 하면 되는지**(최상위 폴더)까지 말해야 그 자리에서 길이 끝나지 않는다.
 *  - `keep` — 확인 바에서 **되돌리는** 버튼의 말이다. 거기에 '취소' 를 쓰면 [공유 취소] 와 한 바에
 *    '취소' 가 둘 서서 어느 것이 되돌리는 쪽인지 알 수 없다(형제 삭제 확인 바는 [폴더만 삭제]·[취소]
 *    라 이 충돌이 없었다).
 */
export const SHARE_ALL_TEACHERS = {
  action: '강사 전원에게 공유',
  cancel: '공유 취소',
  grade: '강사는 보기만 가능',
  scope: '하위 폴더 포함',
  rootOnly: '최상위 폴더에서만 공유할 수 있습니다.',
  keep: '그대로 두기',
};

/**
 * 공유를 내리기 전 확인 바의 문장 — **사라지는 것만 말한다**(오너 규칙 3: 공유는 바로, 취소만 확인).
 *
 * 지워지는 것이 없다는 사실을 함께 적는 이유는 형제 삭제 확인 바와 같다 — 「취소」가 폴더나 시험지가
 * 없어지는 것으로 읽히면, 되돌릴 수 있는 일을 무서워서 안 하게 된다.
 * 첫 문장의 동사는 **버튼 이름과 같다**(「공유 취소」→「공유를 취소합니다」). 확인 바가 누른 버튼과
 * 다른 말을 쓰면 무엇을 확인하는지 한 번 더 읽어야 한다.
 * 하위 폴더를 이름으로 넣는 이유: 공유는 최상위에 걸고 하위로 번진다(계약 §2) — 걷을 때도 함께
 * 걷힌다는 것을 말하지 않으면 「이 폴더 하나만」으로 읽힌다.
 *
 * **개별 공유 이야기를 하지 않는다**(2026-09-10 오너 지적 — 「이 문구 약간 잘못된 것 같지?」).
 * 전에는 「따로 공유한 사람이 있으면 그대로 남습니다」가 붙어 있었다. 계약상으로는 참이지만
 * (§3 Q4 — `items` 를 안 보내면 개별 기록은 그대로다), 이 화면에는 **개별 공유를 만드는 길이 없다**
 * (오너 규칙 1: 전체 공유 / 공유 취소 두 상태뿐). 사용자가 만들 수 없는 것을 말하면 「그런 기능이
 * 있었나」로 읽히고, 바로 앞 문장(「더 이상 볼 수 없습니다」)과도 부딪힌다.
 * 남는 틈 하나 — API 로 직접 걸린 개별 공유가 서버에 있으면 그 교사는 계속 본다. 화면으로는 생길 수
 * 없는 상태라 문장에서 뺐고, 막아야 하면 취소 요청에 `items: []` 를 함께 실으면 된다(putFolderShares).
 * 이름 뒤에 「폴더」를 한 번 더 붙이는 것은 삭제 확인 바와 같은 형식이고, 이름이 무엇으로 끝나든
 * 조사(의)가 맞는다. 이름이 없으면 「이 폴더」로 받친다 — sharedRowTitle 과 같은 태도로,
 * 이름을 모른다고 설명을 지우지 않는다.
 */
export const shareCancelNote = (name) => `${name ? `${name} 폴더` : '이 폴더'}의 강사 전원 공유를 취소합니다.`
  + ' 폴더와 시험지는 지워지지 않습니다.';

/** 무리 제목 — 「내 폴더」와 「공유받은 폴더」. */
export const GROUP_LABEL = { mine: '내 폴더', shared: '공유받은 폴더' };
